import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFormState, useWatch } from 'react-hook-form';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { MAX_METRIC_KEY_LENGTH } from '@gonfalon/metrics/constants';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { getMetricQuery } from '@gonfalon/rest-api/queries/metrics';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { convertToKey } from '@gonfalon/strings/convertToKey';
import { hookFormKeyInvalid } from '@gonfalon/strings/patterns';
import { HelpTooltip } from '@gonfalon/ui/HelpTooltip';
import { FieldGroup, Input, TextField } from '@launchpad-ui/components';

import { RequiredLabel } from '../../CreateMetricModal/components/RequiredLabel';
import type { MetricForm } from '../../CreateMetricModal/formUtils';

import styles from '../../CreateMetricModal/CreateMetricModal.module.css';

const keyFieldHint = <Message id="metrics.metricForm.keyHelp" values={{ code: (chunks) => <code>{chunks}</code> }} />;

interface MetricKeyNameFieldsProps {
  context: UseFormReturn<MetricForm>;
  isEdit?: boolean;
  fullWidth?: boolean;
}

export const MetricKeyNameFields: React.FC<MetricKeyNameFieldsProps> = ({
  context,
  isEdit = false,
  fullWidth = false,
}) => {
  const [autoGenKey, setAutoGenKey] = useState(true);
  const projectKey = useProjectKey();

  const { control, register, setValue, setError, clearErrors, trigger } = context;
  const { errors } = useFormState({ control, name: ['name', 'key'] });

  const handleMetricKeyPrefill = (name: string) => {
    if (autoGenKey && !isEdit) {
      clearErrors('key');
      setValue('key', convertToKey(name), { shouldDirty: true });
    }
  };

  const handleInput = () => {
    setAutoGenKey(false);
  };

  const metricKey = useWatch({ control, name: 'key', exact: true });
  const metricName = useWatch({ control, name: 'name', exact: true });

  const checkMetricKeyExists = async (value: string) => {
    if (isEdit || value === '') {
      return true;
    } else {
      try {
        await getQueryClient().fetchQuery(getMetricQuery({ projectKey, metricKey: value }));
        // It's necessary to set an error manually below in the case of the prefill from name
        // in addition to just returning the error message, since just returning it won't cause
        // the error to be shown unless the field has been touched.
        setError('key', { message: msg('metrics.metricForm.keyExists') });
        return msg('metrics.metricForm.keyExists');
      } catch {
        clearErrors('key');
        return true;
      }
    }
  };

  return (
    <div className={fullWidth ? styles.dualInputContainerFullWidth : styles.dualInputContainer}>
      <FieldGroup errorMessage={errors.name?.message}>
        <TextField
          className={fullWidth ? styles.metricKeyInputFullWidth : styles.metricKeyInput}
          onChange={handleMetricKeyPrefill}
        >
          <RequiredLabel label={msg('metrics.metricForm.nameLabel')} htmlFor="name" />
          <Input
            data-test-id="metric-name"
            data-1p-ignore
            autoComplete="off"
            value={metricName}
            {...register('name', { required: msg('metrics.metricForm.nameRequired') })}
            placeholder={msg('metrics.metricForm.namePlaceholder')}
            onBlur={async (e) => {
              await trigger('name');
              if (autoGenKey) {
                await checkMetricKeyExists(e.target.value);
              }
            }}
          />
        </TextField>
      </FieldGroup>
      <FieldGroup errorMessage={errors.key?.message}>
        <TextField className={fullWidth ? styles.metricKeyInputFullWidth : styles.metricKeyInput} isDisabled={isEdit}>
          <span className={styles.tooltipContainer}>
            <RequiredLabel label={msg('metrics.metricForm.keyLabel')} htmlFor="key" />
            <HelpTooltip placement="right">{keyFieldHint}</HelpTooltip>
          </span>
          <Input
            className={styles.metricKeyPlaceholderText}
            variant="minimal"
            id="key"
            {...register('key', {
              required: msg('metrics.metricForm.keyRequired'),
              pattern: hookFormKeyInvalid,
              maxLength: {
                value: MAX_METRIC_KEY_LENGTH,
                message: msg('metrics.metricForm.keyTooLong', { max: MAX_METRIC_KEY_LENGTH }),
              },
              validate: {
                unique: checkMetricKeyExists,
              },
            })}
            onChange={(e) => {
              clearErrors('key');
              setValue('key', e.target.value, { shouldDirty: true });
            }}
            onInput={handleInput}
            value={metricKey}
            placeholder={msg('metrics.metricForm.keyPlaceholder')}
          />
        </TextField>
      </FieldGroup>
    </div>
  );
};
